/**
 * "How I2Q works": the seven stations from the mail in the shared mailbox to the reply a
 * sales person sends from Outlook, as a Timeline with one icon per station (FLOW_ICON).
 * Horizontal on a laptop, vertical on a phone: the DS Timeline does not wrap, so the
 * orientation follows the narrow media query (false on the server).
 */

import { Icon } from "@norican/icons";
import { Timeline, type TimelineItem, type TimelineTone } from "@norican/react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { FLOW_ICON, type FlowStation } from "@/lib/line-icons";

import { Figure } from "./figure";

export const HOW_IT_WORKS_TITLE = "How I2Q works";

interface Station {
  id: FlowStation;
  title: string;
  description: string;
  tone: TimelineTone;
}

/** The seven stations in the order the work flows; the text is the text of record. */
export const STATIONS: readonly Station[] = [
  {
    id: "arrives",
    title: "The mail arrives",
    description: "A customer writes to the shared mailbox.",
    tone: "default",
  },
  {
    id: "reads",
    title: "I2Q reads the mail",
    description: "It reads the text and the attachments.",
    tone: "accent",
  },
  {
    id: "finds",
    title: "I2Q finds the customer and the items",
    description: "It looks in F&O and in earlier mails.",
    tone: "accent",
  },
  {
    id: "prices",
    title: "I2Q prices the lines",
    description: "It uses the price list of the customer.",
    tone: "accent",
  },
  {
    id: "prepares",
    title: "I2Q prepares the draft",
    description: "The draft and the reply wait in the queue.",
    tone: "accent",
  },
  {
    id: "checks",
    title: "You check and act",
    description: "Read, check, then press the one button.",
    tone: "success",
  },
  {
    id: "sends",
    title: "You send the reply from Outlook",
    description: "I2Q sends nothing.",
    tone: "default",
  },
];

export interface HowItWorksProps {
  caption?: string;
  /**
   * Overrides the media query (the render tests see both forms on the server, where the
   * query is always false). Omit it in the app.
   */
  orientation?: "horizontal" | "vertical";
}

export function HowItWorks({ caption, orientation }: HowItWorksProps) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const items: TimelineItem[] = STATIONS.map((station) => ({
    id: station.id,
    title: station.title,
    description: station.description,
    tone: station.tone,
    icon: <Icon icon={FLOW_ICON[station.id]} size="sm" />,
  }));
  return (
    <Figure name="how-it-works" title={HOW_IT_WORKS_TITLE} caption={caption}>
      {/* DS-GAP(timeline-horizontal-wrap): the horizontal Timeline does not wrap, so the
          figure switches to the vertical form below 768 px by media query. */}
      <Timeline
        orientation={orientation ?? (narrow ? "vertical" : "horizontal")}
        connector="arrow"
        items={items}
        aria-label={HOW_IT_WORKS_TITLE}
      />
    </Figure>
  );
}
